import type { QueryRowType } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useMyConnectorGrants } from "../../hooks/useMyConnectorGrants";
import { useSpaceName } from "../../hooks/useSpaceName";
import { useMakeUrl, routes } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { Avatar, PlaceholderAvatar } from "../Avatar";
import { ClickButton } from "../ClickButton";
import { ConfirmModal } from "../ConfirmModal";
import { ConnectedAccountsTable } from "../ConnectedAccountsTable";
import { SettingsCard, SettingsHeading, SettingsRow } from "../SettingsCard";
import { Text } from "../Text";
import { ToggleVisual } from "../Toggle";

type Grant = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadConnectorDetails>>
>["visitorGrants"][number];

type Props = {
  appId: string;
  providerName: string;
  authorizationMode: string | null;
  grants: readonly Grant[];
  loading: boolean;
  canWrite: boolean;
};

export function VisitorAuthorizationSection({
  appId,
  providerName,
  authorizationMode,
  grants,
  loading,
  canWrite,
}: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [users] = useQuery(queries.allUsers());
  const spaceName = useSpaceName();
  const visitorMode = authorizationMode === "visitor";
  const [myGrants, myGrantsResult] = useMyConnectorGrants({
    enabled: visitorMode,
  });
  const ownGrantIds = new Set(myGrants.map((grant) => grant.id));
  const visibleGrants = canWrite
    ? grants
    : myGrants.filter((grant) => grant.connectedAppId === appId);
  const [confirmingOwnerMode, setConfirmingOwnerMode] = useState(false);
  const setAuthorizationMode = (authorizationMode: "owner" | "visitor") =>
    z.mutate(
      sharedMutators.setConnectorAuthorizationMode({
        connectedAppId: appId,
        authorizationMode,
      })
    );

  return (
    <SettingsCard style={{ marginTop: 16 }}>
      <SettingsRow>
        <SettingsHeading>
          <Text variant="text-regular-md">
            Require each person to connect their own account
          </Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {`When on, each person using an app or workflow connects their own ${providerName} account. This includes people who aren’t members of your 3B workspace. When off, you must authorize one account on this connector for everyone to use. Turning this on disconnects that account.`}
          </Text>
        </SettingsHeading>
        <ClickButton
          role="switch"
          aria-label="Require each person to connect their own account"
          aria-checked={visitorMode}
          disabled={!canWrite || (visitorMode && loading)}
          style={{ flexShrink: 0 }}
          onClick={() => {
            if (visitorMode && grants.length > 0) setConfirmingOwnerMode(true);
            else setAuthorizationMode(visitorMode ? "owner" : "visitor");
          }}
        >
          <ToggleVisual isOn={visitorMode} />
        </ClickButton>
      </SettingsRow>
      {visitorMode && (
        <>
          <ConnectedAccountsTable
            label={canWrite ? "Connected accounts" : "Your connected accounts"}
            loading={canWrite ? loading : myGrantsResult.type !== "complete"}
            emptyText={
              canWrite
                ? "No connected accounts yet"
                : "You haven’t connected an account"
            }
            emptyDescription={
              canWrite
                ? `Accounts appear here when people connect their own ${providerName} account to use an app or workflow.`
                : `Your ${providerName} account appears here after you connect it to use a chat, app, or workflow.`
            }
            accounts={visibleGrants.map((grant) => {
              const user =
                grant.subjectKind === "user"
                  ? users.find((user) => user.id === grant.subjectId)
                  : undefined;
              const label =
                grant.subjectKind === "user"
                  ? (user?.email ?? "Unknown user")
                  : (grant.subjectId ?? "Unknown account");
              return {
                id: grant.id,
                name: user ? userDisplayName(user) : label,
                label,
                description:
                  (grant.spaceIds ?? []).map(spaceName).join(", ") ||
                  "No spaces yet",
                status: grant.status,
                link: user
                  ? makeUrl(routes.modal.member, { id: user.id })
                  : undefined,
                icon: user ? (
                  <Avatar user={user} size={32} />
                ) : (
                  <PlaceholderAvatar size={32} />
                ),
                onDisconnect: () =>
                  z.mutate(
                    (ownGrantIds.has(grant.id)
                      ? sharedMutators.revokeOwnConnectorGrant
                      : sharedMutators.revokeConnectorGrant)({
                      oauthConnectionId: grant.id,
                    })
                  ),
              };
            })}
          />
          {visibleGrants.length > 0 && (
            <Text
              variant="text-small"
              color="var(--text-secondary)"
              style={{ paddingLeft: 8, marginTop: -8 }}
            >
              {canWrite &&
                "Turning this setting off removes these connections. "}
              Disconnecting an account removes it from this connector in all
              spaces.
            </Text>
          )}
        </>
      )}
      <ConfirmModal
        open={confirmingOwnerMode}
        onClose={() => setConfirmingOwnerMode(false)}
        onConfirm={() => {
          setConfirmingOwnerMode(false);
          setAuthorizationMode("owner");
        }}
        title="Remove all connected accounts?"
        description="People will need to reconnect if you turn this setting back on."
        confirmLabel="Turn off"
        destructive
      />
    </SettingsCard>
  );
}
